/**
 * Config Status Badge Component
 * Displays verification status with color-coded chips
 */

import { Chip, useTheme } from '@mui/material';
import { ConfigVerificationStatus } from '../types/configRegistryTypes';

interface ConfigStatusBadgeProps {
  status: ConfigVerificationStatus;
  size?: 'small' | 'medium';
}

// Status display configuration
const STATUS_CONFIG: Record<ConfigVerificationStatus, { label: string; variant: 'filled' | 'outlined' }> = {
  TODO: { label: 'To Do', variant: 'outlined' },
  READY_FOR_QA: { label: 'Ready for QA', variant: 'outlined' },
  APPROVED: { label: 'Approved', variant: 'filled' },
  REJECTED: { label: 'Rejected', variant: 'outlined' },
};

export const ConfigStatusBadge = ({ status, size = 'small' }: ConfigStatusBadgeProps) => {
  const theme = useTheme();

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.READY_FOR_QA;
  
  // Get colors based on status and theme
  const getStatusStyles = () => {
    const isDark = theme.palette.mode === 'dark';
    
    switch (status) {
      case 'APPROVED':
        return {
          backgroundColor: isDark ? theme.palette.success.dark : theme.palette.success.light + '40',
          borderColor: theme.palette.success.main,
          color: isDark ? theme.palette.success.contrastText : theme.palette.success.dark,
        };
      case 'REJECTED':
        return {
          backgroundColor: isDark ? theme.palette.error.dark + '40' : theme.palette.error.light + '30',
          borderColor: theme.palette.error.main,
          color: isDark ? theme.palette.error.light : theme.palette.error.dark,
        };
      case 'READY_FOR_QA':
      default:
        return {
          backgroundColor: isDark ? theme.palette.warning.dark + '40' : theme.palette.warning.light + '40',
          borderColor: theme.palette.warning.main,
          color: isDark ? theme.palette.warning.light : theme.palette.warning.dark,
        };
    }
  };
  
  const styles = getStatusStyles();
  
  return (
    <Chip
      label={config.label}
      size={size}
      variant={config.variant}
      sx={{
        ...styles,
        borderRadius: '6px',
        fontWeight: 500,
        fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        border: '1px solid',
      }}
    />
  );
};

export default ConfigStatusBadge;

